import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from "react";
import Lottie from "lottie-react";
import splashAnimation from "../assets/lottie/splashAnimation";

/**
 * Global loading overlay (full-viewport modal using the splashAnimation lottie).
 *
 *   const {show, hide, wrap} = useLoading();
 *   show("Saving…");
 *   try { await api.save(); } finally { hide(); }
 *
 *   // …or wrap a promise so you can't forget to hide it:
 *   await wrap(api.save(), "Saving…");
 *
 * Multiple overlapping show() calls stack via a ref counter so the overlay
 * only disappears once every caller has released it. This avoids a flicker
 * when several network requests overlap (e.g. save post + reload list).
 */

const LoadingContext = createContext({
  show: () => {},
  hide: () => {},
  wrap: async p => p,
});

export const LoadingProvider = ({children}) => {
  const [visible, setVisible] = useState(false);
  const [label,   setLabel]   = useState("Loading");
  const counter = useRef(0);

  const show = useCallback((nextLabel) => {
    counter.current += 1;
    if (nextLabel) setLabel(nextLabel);
    setVisible(true);
  }, []);

  const hide = useCallback(() => {
    counter.current = Math.max(0, counter.current - 1);
    if (counter.current === 0) {
      setVisible(false);
      setLabel("Loading");
    }
  }, []);

  const wrap = useCallback(async (promise, lbl) => {
    show(lbl);
    try { return await promise; }
    finally { hide(); }
  }, [show, hide]);

  const value = useMemo(() => ({show, hide, wrap}), [show, hide, wrap]);

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {visible && (
        <div
          className="rd-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-busy="true"
          aria-label={`${label} — please wait`}
        >
          <div className="rd-overlay__panel">
            <div className="rd-overlay__lottie" aria-hidden="true">
              <Lottie animationData={splashAnimation} loop autoplay />
            </div>
            <div className="rd-overlay__label">{label}…</div>
          </div>
        </div>
      )}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => useContext(LoadingContext);

export default LoadingContext;
